import { cn } from '@/lib/utils';

import { LINE_CLAMP, sizingClass, TEXT_STYLE } from '../tokens';

import type { catalog } from '@littlebird/action-cards-view';
import type { ComponentFn } from '@littlebird/action-cards-view/react';

// Level 3 is the card title
const LEVEL_CLASS = {
  2: 'text-[length:calc(16*var(--ac-u))] font-semibold leading-[calc(24*var(--ac-u))]',
  3: TEXT_STYLE.title,
  4: `font-medium text-foreground-subtle ${TEXT_STYLE.body}`,
} as const;

// A level outside LEVEL_CLASS's keys (an older client behind a newer catalog) falls back to 3
function resolveHeadingLevel(level: unknown): keyof typeof LEVEL_CLASS {
  return typeof level === 'number' && level in LEVEL_CLASS
    ? (level as keyof typeof LEVEL_CLASS)
    : 3;
}

function lineClampClass(lines: number): string {
  const clamped = Math.min(Math.max(Math.round(lines), 1), LINE_CLAMP.length);
  return LINE_CLAMP[clamped - 1];
}

// Grows to fill a header row, so the badge and buttons after it sit at the end
export const Heading: ComponentFn<typeof catalog, 'Heading'> = ({ props }) => {
  const level = resolveHeadingLevel(props.level);
  return (
    // A heading always fills its header row; `sizing` only adds to that
    // (an explicit `fill` is a no-op)
    // Not an <hN>: in chat the card sits inside markdown, whose `.markdown h3` margins and size would override ours
    <div
      role="heading"
      aria-level={level}
      className={cn(
        'min-w-0 flex-1 break-words text-foreground',
        props.dimmed && 'opacity-[0.55]',
        props.strike && 'line-through',
        lineClampClass(props.lines ?? 2),
        props.textStyle ? TEXT_STYLE[props.textStyle] : LEVEL_CLASS[level],
        sizingClass(props.sizing)
      )}
    >
      {props.text}
      {props.suffix ? (
        <span className="font-normal text-foreground-muted">
          {' '}
          {props.suffix}
        </span>
      ) : null}
    </div>
  );
};
